import type { ReportWidgetItem } from 'widgets/ComposableReportWidget';

interface AugmentItemsForTopNParams {
    items: ReportWidgetItem[];
    topCount: number;
    otherChartItemId: string;
    otherColor?: string;
}

interface BuildTopNChartItemsParams {
    items: ReportWidgetItem[];
    totalAmount?: string;
    topCount: number;
    otherItemId: string;
    otherLabel: string;
    otherColor?: string;
}

/**
 * Marks items beyond the top N as belonging to the shared "Other" chart slice
 * and optionally forces their color to a neutral swatch.
 */
export const augmentItemsForTopNOther = ({
    items,
    topCount,
    otherChartItemId,
    otherColor = 'hsl(0, 0%, 86%)',
}: AugmentItemsForTopNParams): ReportWidgetItem[] =>
    items.map((item, index) => {
        const isBeyondTop = index >= topCount;

        if (!isBeyondTop) {
            return item;
        }

        return {
            ...item,
            color: otherColor,
            meta: {
                ...(item.meta ?? {}),
                chartItemId: otherChartItemId,
            },
        };
    });

/**
 * Builds chart items for Top N + "Other", where "Other" absorbs the remainder
 * either from the full item list or from an explicit total amount.
 *
 * - When totalAmount is provided, it is treated as the overall total.
 * - When totalAmount is omitted, the overall total is the sum of all items.
 */
export const buildTopNChartItemsWithOther = ({
    items,
    totalAmount,
    topCount,
    otherItemId,
    otherLabel,
    otherColor = 'hsl(0, 0%, 86%)',
}: BuildTopNChartItemsParams): ReportWidgetItem[] => {
    if (!items.length) return [];

    const numericValue = (value: number | string): number =>
        typeof value === 'number' ? value : parseFloat(String(value)) || 0;

    const itemsTotal = items.reduce(
        (acc, item) => acc + numericValue(item.primaryValue),
        0,
    );

    const topItems = items.slice(0, topCount);

    const displayedTotal = topItems.reduce(
        (acc, item) => acc + numericValue(item.primaryValue),
        0,
    );

    const overallTotal =
        totalAmount != null
            ? parseFloat(totalAmount) || itemsTotal
            : itemsTotal;

    const otherValue = Math.max(0, overallTotal - displayedTotal);

    if (otherValue <= 0) {
        return topItems;
    }

    const otherItem: ReportWidgetItem = {
        id: otherItemId,
        label: otherLabel,
        primaryValue: otherValue,
        secondaryValues: {},
        meta: { isAggregatedOther: true },
        color: otherColor,
    };

    return [...topItems, otherItem];
};
